import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  Text,
  useToast,
  Flex,
} from "@chakra-ui/react";
import { useState, useEffect } from "react";
import UserService from "../../Pages/User/UserService";
import {
  formatAccessRoleLabel,
  getAccessRoleHint,
  getCompanyHint,
} from "./accessRoleUtils";

export default function UserEditModal({
  user,
  isOpen,
  onClose,
  statusOptions,
  positionOptions,
  roleOptions,
  companyOptions,
  onUpdateSuccess,
}) {
  const toast = useToast();

  const [formData, setFormData] = useState({
    ...user,
    login: user?.login || "",
    name: user?.name || "",
    position: user?.position || null,
    status: user?.status || null,
    role: user?.role || null,
    company: user?.company || null,
    // Alteração AQUI: Carrega o campo de senha com um placeholder não vazio
    password: "********", // Ou qualquer valor que seu backend NÃO interprete como uma senha real para reset
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setFormData({
      ...user,
      login: user?.login || "",
      name: user?.name || "",
      position: user?.position || null,
      status: user?.status || null,
      role: user?.role || null,
      company: user?.company || null,
      // Alteração AQUI: Também no useEffect para quando o usuário muda
      password: "********", // Garante que a senha sempre comece com o placeholder ao abrir/reabrir o modal
    });
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleOptionChange = (e, type) => {
    const { value } = e.target;
    const optionsMap = {
      status: statusOptions,
      position: positionOptions,
      role: roleOptions,
      company: companyOptions,
    };
    const selectedOption = optionsMap[type]?.find(
      (opt) => String(opt.optionsId) === value
    );

    setFormData((prevData) => ({
      ...prevData,
      [type]: selectedOption || null,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name?.trim()) {
      toast({
        title: "Nome obrigatório",
        description: "Informe o nome do usuário.",
        status: "warning",
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    if (!formData.position || !formData.status || !formData.role || !formData.company) {
      toast({
        title: "Campos obrigatórios",
        description: "Selecione cargo, empresa, nível de acesso e status.",
        status: "warning",
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = { ...formData };
      delete payload.authorities;

      // Lógica de manipulação da senha no payload
      // Se o usuário MUDOU o placeholder, envie a nova senha
      // Se o usuário DEIXOU o placeholder, NÃO envie o campo password
      if (payload.password === "********") {
        delete payload.password; // Remove o campo se ainda for o placeholder
      }

      // Garanta que os objetos de opções sejam enviados no formato correto (apenas id e description)
      if (payload.position) {
        payload.position = {
          optionsId: payload.position.optionsId,
          description: payload.position.description,
        };
      }
      if (payload.status) {
        payload.status = {
          optionsId: payload.status.optionsId,
          description: payload.status.description,
        };
      }
      if (payload.role) {
        payload.role = {
          optionsId: payload.role.optionsId,
          description: payload.role.description,
        };
      }
      if (payload.company) {
        payload.company = {
          optionsId: payload.company.optionsId,
          description: payload.company.description,
        };
      }

      await UserService.updateUser(payload);

      toast({
        title: "Usuário atualizado.",
        description: "As informações do usuário foram salvas com sucesso.",
        status: "success",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
      onUpdateSuccess();
      onClose();
    } catch (error) {
      console.error("Erro ao atualizar usuário:", error);
      toast({
        title: "Erro ao atualizar.",
        description:
          error.response?.data?.message ||
          "Não foi possível salvar as alterações do usuário.",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const commonInputStyles = {
    bg: "gray.50",
    borderColor: "gray.200",
    _hover: { borderColor: "gray.300" },
    _focus: { borderColor: "red.500", boxShadow: "0 0 0 1px red.500" },
    borderRadius: "md",
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
      <ModalOverlay />
      <ModalContent borderRadius="xl" overflow="hidden">
        <ModalHeader
          bg="red.600"
          color="white"
          py={4}
          textAlign="center"
          fontSize="2xl"
          fontWeight="bold"
        >
          Editar Usuário
        </ModalHeader>
        <ModalCloseButton color="white" />
        <ModalBody p={6}>
          <form id="edit-user-form" onSubmit={handleSubmit}>
            <FormControl mb={4}>
              <FormLabel fontWeight="bold" color="gray.700">
                Login
              </FormLabel>
              <Input
                name="login"
                value={formData.login}
                onChange={handleInputChange}
                readOnly
                disabled
                {...commonInputStyles}
                _disabled={{ opacity: 0.7, cursor: "not-allowed" }}
              />
            </FormControl>

            <FormControl mb={4}>
              <FormLabel fontWeight="bold" color="gray.700">
                Nome
              </FormLabel>
              <Input
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                {...commonInputStyles}
                placeholder="Nome completo do usuário"
              />
            </FormControl>

            {/* Campo para a Senha */}
            <FormControl mb={4}>
              <FormLabel fontWeight="bold" color="gray.700">
                Senha (preencha para alterar)
              </FormLabel>
              <Input
                name="password"
                type="password"
                value={formData.password}
                onChange={handleInputChange}
                {...commonInputStyles}
                // O placeholder só aparece se o value estiver vazio, mas aqui o value já vem com '********'
                // placeholder="********" // Pode ser removido, pois o value já preenche
              />
            </FormControl>

            <FormControl mb={4}>
              <FormLabel fontWeight="bold" color="gray.700">
                Cargo
              </FormLabel>
              <Select
                name="position"
                value={
                  formData.position
                    ? String(formData.position.optionsId)
                    : ""
                }
                onChange={(e) => handleOptionChange(e, "position")}
                {...commonInputStyles}
              >
                <option value="">Selecione o Cargo</option>
                {positionOptions.map((opt) => (
                  <option key={opt.optionsId} value={String(opt.optionsId)}>
                    {opt.description}
                  </option>
                ))}
              </Select>
            </FormControl>

            <FormControl mb={4}>
              <FormLabel fontWeight="bold" color="gray.700">
                Empresa vinculada
              </FormLabel>
              <Select
                name="company"
                value={
                  formData.company ? String(formData.company.optionsId) : ""
                }
                onChange={(e) => handleOptionChange(e, "company")}
                {...commonInputStyles}
              >
                <option value="">Selecione a Empresa</option>
                {companyOptions.map((opt) => (
                  <option key={opt.optionsId} value={String(opt.optionsId)}>
                    {opt.description}
                  </option>
                ))}
              </Select>
              <Text mt={2} fontSize="sm" color="gray.600">
                {getCompanyHint(
                  formData.role?.description,
                  formData.company?.description
                )}
              </Text>
            </FormControl>

            <FormControl mb={4}>
              <FormLabel fontWeight="bold" color="gray.700">
                Nível de Acesso (perfil)
              </FormLabel>
              <Select
                name="role"
                value={formData.role ? String(formData.role.optionsId) : ""}
                onChange={(e) => handleOptionChange(e, "role")}
                {...commonInputStyles}
              >
                <option value="">Selecione o Nível de Acesso</option>
                {roleOptions.map((opt) => (
                  <option key={opt.optionsId} value={String(opt.optionsId)}>
                    {formatAccessRoleLabel(opt.description)}
                  </option>
                ))}
              </Select>
              <Text mt={2} fontSize="sm" color="gray.600">
                {getAccessRoleHint(formData.role?.description)}
              </Text>
            </FormControl>

            <FormControl mb={4}>
              <FormLabel fontWeight="bold" color="gray.700">
                Status
              </FormLabel>
              <Select
                name="status"
                value={formData.status ? String(formData.status.optionsId) : ""}
                onChange={(e) => handleOptionChange(e, "status")}
                {...commonInputStyles}
              >
                <option value="">Selecione o Status</option>
                {statusOptions.map((opt) => (
                  <option key={opt.optionsId} value={String(opt.optionsId)}>
                    {opt.description}
                  </option>
                ))}
              </Select>
            </FormControl>
          </form>
        </ModalBody>
        <ModalFooter
          bg="gray.50"
          borderBottomRadius="xl"
          p={4}
          justifyContent="flex-end"
        >
          <Flex gap={3}>
            <Button
              variant="outline"
              onClick={onClose}
              isDisabled={isSubmitting}
              colorScheme="gray"
            >
              Cancelar
            </Button>
            <Button
              form="edit-user-form"
              type="submit"
              colorScheme="red"
              isLoading={isSubmitting}
              loadingText="Salvando"
            >
              Salvar
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
